import * as React from 'react'
import { ThemeProvider } from '@emotion/react'
import { Alert, Box, Button, Container, IconButton, Snackbar, Tooltip, Typography } from '@mui/material'
import MUIDataTable from 'mui-datatables'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import theme from '../theme'
import api from '../services/api'
import { useAuth } from '../auth/AuthProvider'
import { useNavigate } from 'react-router-dom'
import FormDialog from './FormDialog'

export default function AthenaConfig() {
    const navigate = useNavigate()
    const auth = useAuth()
    const [handlerDialog, setHandlerDialog] = React.useState(false)
    const [editData, setEditData] = React.useState(null)
    const [update, setUpdate] = React.useState(false)
    const [data, setData] = React.useState([])
    const [snack, setSnack] = React.useState({ open: false, message: '' })

    React.useEffect(() => {
        setUpdate(false)
        updateData()
    }, [handlerDialog, update])

    React.useEffect(() => {
        document.title = "Horus - Athena Config"
    }, [])

    const updateData = () => {
        api.get('athena-config/')
            .then(res => setData(res.data))
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    navigate('/401')
                }
                setData([])
            })
    }

    const deleteItem = (id, name) => {
        const conf = confirm(`Deseja realmente desativar a config "${name}"?`)
        if (!conf) return
        api.delete(`athena-config/${id}`).then(() => {
            setUpdate(true)
        }).catch(() => alert('Algum erro aconteceu...'))
    }

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setSnack({ open: true, message: 'Copiado!' })
        })
    }

    const columns = [
        {
            name: 'id',
            label: 'Id',
            options: { display: false }
        },
        {
            name: 'cod_empresa',
            label: 'Empresa',
            options: {
                filter: true,
                sort: true,
                customBodyRender: (value) => (
                    <Typography fontSize={14} fontWeight="bold">
                        {value === 0 ? '0 (Default)' : value}
                    </Typography>
                )
            }
        },
        {
            name: 'name',
            label: 'Nome',
            options: { filter: true, sort: true }
        },
        {
            name: 'athena_url',
            label: 'URL',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value) => (
                    <code style={{ fontSize: '0.75rem' }}>
                        {value && value.length > 40 ? value.substring(0, 40) + '...' : value}
                    </code>
                )
            }
        },
        {
            name: 'webhook_token',
            label: 'Token',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <code style={{ fontSize: '0.75rem' }}>
                            {value ? value.substring(0, 12) + '...' : ''}
                        </code>
                        <Tooltip title="Copiar token">
                            <IconButton size="small" onClick={() => copyToClipboard(value)}>
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            }
        },
        {
            name: 'created_at',
            label: 'Criado em',
            options: { filter: false, sort: true }
        },
        {
            name: 'actions',
            label: 'Acoes',
            options: {
                filter: false,
                sort: false,
                customBodyRenderLite: (dataIndex) => {
                    const row = data[dataIndex]
                    if (!row) return null
                    return (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button
                                disabled={!auth.hasPerm('problem.edit')}
                                sx={{ color: theme.palette.text.primary, minWidth: 'auto' }}
                                onClick={() => {
                                    setEditData(row)
                                    setHandlerDialog(true)
                                }}
                            >
                                <EditIcon />
                            </Button>
                            <Button
                                disabled={!auth.hasPerm('problem.edit')}
                                sx={{ color: theme.palette.text.primary, minWidth: 'auto' }}
                                onClick={() => deleteItem(row.id, row.name)}
                            >
                                <DeleteIcon />
                            </Button>
                        </Box>
                    )
                }
            }
        }
    ]

    const options = {
        filterType: 'checkbox',
        rowsPerPageOptions: [30, 50, 100],
        rowsPerPage: 30,
        rowHover: false,
        print: false,
        selectableRows: 'none',
        download: false,
        customToolbar: () => (
            <IconButton
                disabled={!auth.hasPerm('problem.edit')}
                onClick={() => { setEditData(null); setHandlerDialog(true) }}
                sx={{ color: theme.palette.text.primary }}
            >
                <AddIcon />
            </IconButton>
        ),
    }

    return (
        <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
            <ThemeProvider theme={theme}>
                <Snackbar
                    open={snack.open}
                    autoHideDuration={2000}
                    onClose={() => setSnack({ open: false, message: '' })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert severity="success" onClose={() => setSnack({ open: false, message: '' })}>
                        {snack.message}
                    </Alert>
                </Snackbar>

                <FormDialog
                    open={handlerDialog}
                    editData={editData}
                    handlerClose={() => {
                        setHandlerDialog(false)
                        setEditData(null)
                    }}
                    updateData={updateData}
                />

                <Box sm={12} lg={12}>
                    <MUIDataTable
                        title={"Athena Config"}
                        data={data}
                        columns={columns}
                        options={options}
                    />
                </Box>
            </ThemeProvider>
        </Container>
    )
}
